

import { useState, useEffect, useRef, useCallback } from "react";
import { showToast } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getUrlParam(name) {
  return new URLSearchParams(window.location.search).get(name);
}

function decodeDataParam(raw) {
  if (!raw) return null;
  try {
    const clean = raw.replace(/-/g, "+").replace(/_/g, "/").replace(/ /g, "+");
    const parsed = JSON.parse(atob(clean));
    return Array.isArray(parsed) ? parsed[0] : parsed;
  } catch (e) {
    console.warn("[Payment] decode error:", e.message);
    return null;
  }
}

// ─── Status map ───────────────────────────────────────────────────────────────
const STATUS = {
  12: null,
  21: "processing",
  13: "overpaid",
  14: "underpaid",
  16: "success",
  19: "success_over",
  20: "success_under",
  30: "failed",
  48: "expired",
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function Invoice() {
  document.title = "Checkout";

  // ── Data ──────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [parseError, setParseError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [obj, setObj] = useState(null);
  const [companyName, setCompanyName] = useState(
    localStorage.getItem(SITENAMEALIAS + "_companyName") || "PayBito"
  );

  // Display values
  const [coinName, setCoinName] = useState("");
  const [coinImage, setCoinImage] = useState("");
  const [currencyDisplay, setCurrencyDisplay] = useState("");
  const [payAmount, setPayAmount] = useState("0.000000");
  const [txAddress, setTxAddress] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [memo, setMemo] = useState("");
  const [exchangeName, setExchangeName] = useState(
    localStorage.getItem("bill_bitcoins_exchange") || "Wallet"
  );

  // Merchant info
  const [merchantName, setMerchantName] = useState("");
  const [merchantAddr, setMerchantAddr] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [supportPhone, setSupportPhone] = useState("");

  // Order table type
  const [tableType, setTableType] = useState("paynow");
  const [paynowRows, setPaynowRows] = useState({
    desc: "",
    qty: 1,
    price: "",
    total: "",
    currency: "",
  });
  const [billItems, setBillItems] = useState([]);
  const [billTotal, setBillTotal] = useState("");
  const [catalogRow, setCatalogRow] = useState({
    desc: "",
    price: "",
    shipping: "",
    tax: "",
    total: "",
  });
  const [catalogTotal, setCatalogTotal] = useState("");
  const [processingFeeRow, setProcessingFeeRow] = useState(null);

  // Timer state
  const [timerSecs, setTimerSecs] = useState(900);
  const [isPriceLoaded, setIsPriceLoaded] = useState(false);
  const timerRef = useRef(null);
  const invoiceRef = useRef({ id: null, merchantId: null });
  // ── FIX: refs to break stale closure cycle between refreshPrice & startTimer
  const refreshPriceRef = useRef(null);
  const startTimerRef = useRef(null);
  const chargeIdRef = useRef("");

  // Status
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [amountDue, setAmountDue] = useState("");
  const [partPaid, setPartPaid] = useState("");
  const [returnUrl, setReturnUrl] = useState("");
  const [remainingAmount, setRemainingAmount] = useState(null);
  const destroyTimer = useRef(false);

  // Copy
  const [copied, setCopied] = useState(false);
  const [memoCopied, setMemoCopied] = useState(false);
  const [cryptoEquivalent, setCryptoEquivalent] = useState("");
  const [cryptoMarketPrice, setCryptoMarketPrice] = useState(null);
  const [urlCrypto, setUrlCrypto] = useState(getUrlParam("crypto") || "");
  const [urlNetwork, setUrlNetwork] = useState(getUrlParam("network") || "");

  // ── Checkout profile flow: individual order rows ───────────────────────────
  const [orderProductRows, setOrderProductRows] = useState([]);
  const [orderHandlingFee, setOrderHandlingFee] = useState(null);
  const [orderTaxFee, setOrderTaxFee] = useState(null);
  const [orderDiscount, setOrderDiscount] = useState(null);
  const [orderCurrency, setOrderCurrency] = useState("USD");

  // ── Favicon ────────────────────────────────────────────────────────────────
  function applyFavicon(href) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }
  function faviconFallback() {
    const logo = localStorage.getItem("bill_bitcoins_exchangeLogo");
    if (logo) applyFavicon(logo);
  }

  // ── Broker info ────────────────────────────────────────────────────────────
  useEffect(() => {
    document.title = "Checkout";
    const token = getAuthToken();
    const uuid = getAuthUuid();
    if (token && uuid) {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", `${WEBSERVICE}/merchant/getProfilePicture/${uuid}`, true);
      xhr.responseType = "blob";
      xhr.setRequestHeader("Authorization", `bearer ${token}`);
      xhr.setRequestHeader("UUID", uuid);
      xhr.onload = () =>
        xhr.status === 200 && xhr.response?.size > 0
          ? applyFavicon(URL.createObjectURL(xhr.response))
          : faviconFallback();
      xhr.onerror = faviconFallback;
      xhr.send();
    } else faviconFallback();

    let brokerId = null;
    const rawD = getUrlParam("data");
    if (rawD) {
      try {
        const dec = JSON.parse(atob(rawD.replace(/ /g, "+")));
        const o = Array.isArray(dec) ? dec[0] : dec;
        brokerId = o.brokerId || o.broker_id || null;
      } catch (e) { }
    }
    if (!brokerId) {
      brokerId =
        new URLSearchParams(window.location.search).get("brokerId") ||
        localStorage.getItem("bill_bitcoins_broker_id") ||
        localStorage.getItem("bill_bitcoins_brokerId");
    }
    if (brokerId) {
      localStorage.setItem("bill_bitcoins_broker_id", brokerId);
      fetch(
        `https://accounts.paybito.com/api/home/getBrokerWiseExchangeInfo?brokerId=${brokerId}`
      )
        .then((r) => r.json())
        .then((res) => {
          const info = res.value?.[0] || {};
          const name = info.exchange || "";
          setExchangeName(name);
          localStorage.setItem("bill_bitcoins_exchange", name);
          localStorage.setItem(
            "bill_bitcoins_exchangeLogo",
            info.exchange_logo || ""
          );
          if (res.color) {
            localStorage.setItem(
              "bill_bitcoins_navbarColour",
              res.color.navbarColor
            );
            localStorage.setItem(
              "bill_bitcoins_buttonColour",
              res.color.buttonColor
            );
          }
        })
        .catch(() => { });
    }
  }, []);

  // ── Timer Functions ────────────────────────────────────────────────────────
  // FIX: empty deps [] — uses startTimerRef to avoid stale closure
  const refreshPrice = useCallback((decoded, currStr) => {
    const assetCode = getAssetCode(decoded.currency_id, decoded);
    fetch(`${WEBSERVICE}/merchant/getMarketPrice?counter=${assetCode}`)
      .then((r) => r.json())
      .then((res) => {
        if (res.error !== "0") {
          // FIX: call via ref, not direct reference
          startTimerRef.current?.(decoded, currStr);
          return;
        }

        const baseAmt =
          parseFloat(decoded.fiat_amount || decoded.item_price || 0) /
          parseFloat(res.price);
        const qty = decoded.quantity || 1;
        let total = baseAmt * parseFloat(qty);

        const cryptoPrice = parseFloat(res.price);
        setCryptoMarketPrice(cryptoPrice);

        if (cryptoPrice > 0 && decoded.fiat_amount) {
          const converted = (
            parseFloat(decoded.fiat_amount) / cryptoPrice
          ).toFixed(8);
          const networkLabel = decoded.network
            ? ` (${decoded.network === "ERC"
              ? "ERC-20"
              : decoded.network === "TRC"
                ? "TRC-20"
                : decoded.network
            })`
            : "";
          setCryptoEquivalent(
            `${converted} ${decoded.currency || assetCode}${networkLabel}`
          );
        }

        const isPFee =
          decoded.is_processing_fee_applied == 1 ||
          decoded.is_processing_fee_applied == "1";

        if (isPFee) {
          fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              merchantId: String(decoded.merchant_id),
              currencyId: String(decoded.currency_id),
              amount: parseFloat(total.toFixed(6)),
            }),
          })
            .then((r) => r.json())
            .then((feeData) => {
              if (feeData.error === "0") {
                const newAmount = parseFloat(feeData.totalAmount).toFixed(6);
                setPayAmount(`${newAmount} ${currStr}`);
                setProcessingFeeRow({
                  pct: feeData.txnCharge,
                  amount: parseFloat(feeData.transactionFee).toFixed(6),
                });
                setPaynowRows((prev) => ({ ...prev, total: newAmount }));
              }
            })
            .catch(() => { })
            .finally(() => {
              localStorage.setItem(
                "bill_bitcoins_invoice_timer_start_at",
                "900"
              );
              setTimerSecs(900);
              // FIX: call via ref
              startTimerRef.current?.(decoded, currStr);
            });
        } else {
          const newAmount = total.toFixed(6);
          setPayAmount(`${newAmount} ${currStr}`);
          setPaynowRows((prev) => ({ ...prev, total: newAmount }));

          localStorage.setItem("bill_bitcoins_invoice_timer_start_at", "900");
          setTimerSecs(900);
          // FIX: call via ref
          startTimerRef.current?.(decoded, currStr);
        }
      })
      .catch(() => {
        // FIX: call via ref
        startTimerRef.current?.(decoded, currStr);
      });
  }, []); // FIX: empty deps

  // FIX: sync ref every render so it's always fresh
  refreshPriceRef.current = refreshPrice;

  // FIX: empty deps [] — uses refreshPriceRef to avoid stale closure
  const startTimer = useCallback((decoded, currStr) => {
    console.log("startTimer called");

    destroyTimer.current = false;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    let savedSecs = localStorage.getItem(
      "bill_bitcoins_invoice_timer_start_at"
    );
    let startSecs = 900;

    if (savedSecs && !isNaN(parseInt(savedSecs))) {
      startSecs = parseInt(savedSecs);
      if (startSecs < 0) startSecs = 900;
      if (startSecs > 900) startSecs = 900;
    }

    console.log("Starting timer with seconds:", startSecs);
    setTimerSecs(startSecs);

    timerRef.current = setInterval(() => {
      if (destroyTimer.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }

      setTimerSecs((prevSecs) => {
        const newSecs = prevSecs - 1;
        localStorage.setItem(
          "bill_bitcoins_invoice_timer_start_at",
          String(newSecs)
        );

        if (newSecs <= 0) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          localStorage.removeItem("bill_bitcoins_invoice_timer_start_at");
          // FIX: call via ref inside interval to avoid stale closure
          setTimeout(() => refreshPriceRef.current?.(decoded, currStr), 0);
          return 0;
        }

        return newSecs;
      });
    }, 1000);
  }, []); // FIX: empty deps

  // FIX: sync ref every render so it's always fresh
  startTimerRef.current = startTimer;

  // ── Status polling ─────────────────────────────────────────────────────────
  function pollStatus() {
    const id = invoiceRef.current.id;
    const mid = invoiceRef.current.merchantId;
    const chargeId =
      chargeIdRef.current ||
      getUrlParam("charge_id") ||
      getUrlParam("chargeId") ||
      "";

    if ((!id || !mid) && !chargeId) return;

    const payload = {
      invoice_id: id,
      merchant_id: mid,
      ...(chargeId && { charge_id: chargeId }),
    };

    fetch(`${WEBSERVICE}/merchant/get_invoice_status_paid_amount`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => {
        const d = Array.isArray(data) ? data[0] : data;
        if (d.error != 0) {
          setTimeout(pollStatus, 30000);
          return;
        }
        const st = d.status;//TODO : uncomment this
        //const st = 14;//TODO : delete this after testing
        window.invoice_status = st;

        if (st == 21 && d.amount_paid)
          setPartPaid(`${parseFloat(d.amount_paid)} paid so far.`);

        const terminal = [13, 14, 16, 19, 20, 30, 48].includes(st);
        if (terminal) {
          destroyTimer.current = true;
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          localStorage.removeItem("bill_bitcoins_invoice_timer_start_at");
          const paid = d.amount_paid ? parseFloat(d.amount_paid) : 0;
          const due = d.amount_to_be_paid ? parseFloat(d.amount_to_be_paid) : 0;
          setAmountPaid(paid ? `${paid}` : "");
          setAmountDue(due ? `${due}` : "");
          // For underpaid statuses, show remaining = due - paid
          if (st === 14 || st === 20) {
            const remaining = Math.max(0, due - paid);
            setRemainingAmount(remaining.toFixed(8));
          }
          setPaymentStatus(STATUS[st] || "unknown");
        } else {
          setTimeout(pollStatus, 30000);
        }
      })
      .catch(() => setTimeout(pollStatus, 30000));
  }

  // ── Copy address ───────────────────────────────────────────────────────────
  function copyAddress() {
    navigator.clipboard.writeText(txAddress).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  // ── Wallet redirect ────────────────────────────────────────────────────────
  const [walletLoading, setWalletLoading] = useState(false);
  function redirectToExchange() {
    if (walletLoading) return;
    setWalletLoading(true);
    const ALIAS = "bill_bitcoins";
    const accessToken = getAuthToken();
    const uuid = getAuthUuid();
    if (!accessToken || !uuid) {
      const domain = localStorage.getItem(`${ALIAS}_brokerDomain`);
      window.open(domain || `${window.location.origin}/my-wallet`, "_blank");
      setWalletLoading(false);
      return;
    }
    fetch(`${WEBSERVICE}/merchant/auto-login/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        UUID: uuid,
      },
      body: JSON.stringify({
        merchantId: localStorage.getItem(`${ALIAS}_merchant_id`),
        brokerId: localStorage.getItem(`${ALIAS}_broker_id`),
        uuid,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error?.error_data != "0") {
          alert(data.error?.error_message || "Failed.");
          setWalletLoading(false);
          return;
        }
        return fetch("https://accounts.paybito.com/api/user/GetUserDetails", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `BEARER ${data.token}`,
          },
          body: JSON.stringify({ uuid: data.uuid, userId: data.userId }),
        })
          .then((r) => r.json())
          .then((d) => {
            const u = d.userResult;
            const params = new URLSearchParams({
              brokerId: u.brokerId,
              uuid: u.uuid,
              email: u.email,
              token: data.token,
              callFrom: "payments",
            });
            window.open(
              `${window.location.origin}/autologin?${params}`,
              "_blank"
            );
            setWalletLoading(false);
          });
      })
      .catch(() => {
        alert("Failed to connect to wallet.");
        setWalletLoading(false);
      });
  }

  // ── Load saved timer on mount ──────────────────────────────────────────────
  useEffect(() => {
    const savedTimer = localStorage.getItem(
      "bill_bitcoins_invoice_timer_start_at"
    );
    if (savedTimer && !isNaN(parseInt(savedTimer))) {
      let savedSecs = parseInt(savedTimer);
      if (savedSecs >= 0 && savedSecs <= 900) {
        setTimerSecs(savedSecs);
      }
    }
  }, []);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      destroyTimer.current = true;
    };
  }, []);

  // ── Main data load ─────────────────────────────────────────────────────────
  useEffect(() => {
    const rawData = getUrlParam("data");
    if (!rawData) {
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    const decoded = decodeDataParam(rawData);
    console.log("Decoded Data =>", decoded);
    if (!decoded) {
      setShowErrorModal(true);
      setLoading(false);
      return;
    }

    // Extract receiving address from charge data
    if (decoded.receivingAddress) {
      console.log("Setting receiving address:", decoded.receivingAddress);
      setTxAddress(decoded.receivingAddress);
    }

    setObj(decoded);
    invoiceRef.current.id = decoded.invoice_id;
    invoiceRef.current.merchantId = decoded.merchant_id;
    chargeIdRef.current = decoded.chargeId || decoded.charge_id || "";

    const crypto = decoded.crypto || "";
    const network = decoded.network || "";
    const fiatAmt = parseFloat(decoded.amount || 0);

    //console.log('Object:', obj);

    if (crypto) setUrlCrypto(crypto);
    if (network) setUrlNetwork(network);

    if (decoded.browser_redirect) setReturnUrl(decoded.browser_redirect);

    const cid = decoded.currency_id;
    const assetCode = getAssetCode(cid, decoded);
    const currStr = assetCode + (decoded.network ? ` ${decoded.network}` : "");
    setCurrencyDisplay(currStr);
    setCoinName(
      getAssetName(cid, decoded) +
      (decoded.network ? ` (${decoded.network})` : "")
    );
    setCoinImage(getAssetImage(cid));

    if (!decoded.receivingAddress && decoded.transaction_address) {
      setTxAddress(decoded.transaction_address);
    }

    if (cid == 8 || cid == 14) setMemo(`Memo: ${decoded.invoice_id}`);

    setMerchantName(decoded.organization_name || "");
    setSupportEmail(decoded.support_email || "");
    setSupportPhone(decoded.support_phone || "");
    const addr =
      decoded.separate_invoice_location === "yes"
        ? [
          decoded.inv_address_line_1,
          decoded.inv_address_line_2,
          decoded.inv_city,
          decoded.inv_state,
          decoded.inv_country,
          decoded.inv_zip,
        ]
        : [
          decoded.address_line_1,
          decoded.address_line_2,
          decoded.city,
          decoded.state,
          decoded.country,
          decoded.zip,
        ];
    setMerchantAddr(addr.filter(Boolean).join(", "));

    // ── Checkout profile flow (Guest Checkout) ───────────────────────────────
    const isCheckoutFlow =
      !!(decoded.paymentId || decoded.payment_id) && !decoded.invoice_id;
    if (isCheckoutFlow) {
      const pId = decoded.paymentId || decoded.payment_id || "";
      const mId = decoded.merchantId || decoded.merchant_id || "";

      if (crypto) {
        const cryptoMeta = {
          BTC: { name: "Bitcoin", id: 2 },
          ETH: { name: "Ethereum", id: 3 },
          BCH: { name: "Bitcoin Cash", id: 4 },
          LTC: { name: "Litecoin", id: 7 },
          HCX: { name: "Hashcash Coin", id: 8 },
          XRP: { name: "Ripple", id: 14 },
          USDT: { name: "Tether", id: 16 },
          DOGE: { name: "Dogecoin", id: 124 },
          USDC: { name: "USD Coin", id: 148 },
        };
        const meta = cryptoMeta[crypto] || {};
        const networkLabel = network ? ` (${network})` : "";
        setCoinName(`${meta.name || crypto}${networkLabel}`);
        setCoinImage(getAssetImage(meta.id));
        setCurrencyDisplay(`${crypto}${network ? ` ${network}` : ""}`);
      }

      const profileFetch =
        pId && mId
          ? fetch(
            `${WEBSERVICE}/payment/profileDetailsById?id=${encodeURIComponent(
              pId
            )}&merchantId=${encodeURIComponent(mId)}`
          )
            .then((r) => r.json())
            .catch(() => null)
          : Promise.resolve(null);

      profileFetch.then((profileData) => {
        let displayCurrency = "USD"; // hoisted so the crypto fetch below can read it

        if (
          profileData &&
          (profileData.error === 0 || profileData.error === "0")
        ) {
          const prods = profileData.products || [];
          const shipping = profileData.shippingProfiles?.[0] || {};
          const discountsList = profileData.discounts || [];
          const hPct = parseFloat(shipping.handlingFeeValue || 0);
          const tPct = parseFloat(shipping.taxRate || 0);

          // Get all price currencies from products
          const allPriceCurrencies = new Set(
            prods.flatMap((p) =>
              (p.prices || []).flatMap((pr) =>
                (pr.currencies || []).map((c) => c.currency)
              )
            )
          );

          // Resolve display currency: use product's default currency directly
          const CURRENCY_SYMBOLS = {
            USD: "$", EUR: "€", GBP: "£", JPY: "¥", INR: "₹",
            AUD: "A$", CAD: "C$", CHF: "CHF", SGD: "S$", HKD: "HK$",
            MYR: "RM", NZD: "NZ$", BRL: "R$", ZAR: "R",
            BTC: "₿", ETH: "Ξ", USDT: "USDT", USDC: "USDC",
            LTC: "Ł", BCH: "BCH", XRP: "XRP", DOGE: "Ð", HCX: "HCX",
          };

          let needsFxConversion = false;
          let firstCode = null;
          // displayCurrency already declared above

          if (allPriceCurrencies.size === 0) {
            displayCurrency = "USD";
          } else {
            // Always use the product's default currency — no FX conversion
            const firstPrice = prods?.[0]?.prices?.[0];
            const defaultCurr =
              firstPrice?.currencies?.find((c) => c.default === true) ||
              firstPrice?.currencies?.[0];
            displayCurrency = defaultCurr?.currency || "USD";
          }
          setOrderCurrency(displayCurrency);

          // If FX conversion needed, fetch rate and convert
          if (needsFxConversion && firstCode) {
            fetch(`${WEBSERVICE}/merchant/getMarketPrice?counter=${firstCode}`)
              .then((r) => r.json())
              .then((priceRes) => {
                if (
                  (priceRes.error === "0" || priceRes.error === 0) &&
                  parseFloat(priceRes.price) > 0
                ) {
                  const rate = parseFloat(priceRes.price);

                  let subtotal = 0;
                  const prodRows = prods.map((p) => {
                    const priceObj = p.prices?.[0];
                    const ce =
                      priceObj?.currencies?.find(
                        (c) => c.currency === firstCode
                      ) || priceObj?.currencies?.[0];
                    // DIVIDE by rate to convert to USD
                    const base =
                      parseFloat(
                        p.basePrice || p.price || p.amount || ce?.amount || 0
                      ) / rate;
                    subtotal += base;
                    return { name: p.name || "Product", base };
                  });
                  setOrderProductRows(prodRows);

                  let handlingTotal = 0;
                  if (hPct > 0) {
                    handlingTotal = parseFloat(
                      ((subtotal * hPct) / 100).toFixed(2)
                    );
                    setOrderHandlingFee({ pct: hPct, amount: handlingTotal });
                  }

                  let taxTotal = 0;
                  if (tPct > 0) {
                    taxTotal = parseFloat(((subtotal * tPct) / 100).toFixed(2));
                    setOrderTaxFee({ pct: tPct, amount: taxTotal });
                  }

                  let runningTotal = parseFloat(
                    (subtotal + handlingTotal + taxTotal).toFixed(2)
                  );

                  let discountAmt = 0;
                  if (discountsList.length > 0) {
                    let bestDiscount = null;
                    let bestAmt = 0;
                    for (const d of discountsList) {
                      const minVal = parseFloat(d.minimumCartValue || 0);
                      const pct = parseFloat(
                        d.discountPercentage || d.discountPercent || 0
                      );
                      if (runningTotal >= minVal && pct > 0) {
                        const amt = (runningTotal * pct) / 100;
                        if (!bestDiscount || amt > bestAmt) {
                          bestDiscount = d;
                          bestAmt = amt;
                        }
                      }
                    }
                    if (bestDiscount) {
                      discountAmt = parseFloat(bestAmt.toFixed(2));
                      setOrderDiscount({
                        pct:
                          bestDiscount.discountPercentage ||
                          bestDiscount.discountPercent,
                        amount: discountAmt,
                        label: bestDiscount.profileName || "Discount",
                      });
                      runningTotal = parseFloat(
                        (runningTotal - discountAmt).toFixed(2)
                      );
                    }
                  }

                  const hasFee =
                    profileData.isProcessingFeeApplied == 1 ||
                    profileData.isProcessingFeeApplied === "1" ||
                    profileData.isProcessingFeeApplied === true;
                  if (hasFee && runningTotal > 0) {
                    fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        merchantId: String(mId),
                        currencyId: "1",
                        amount: runningTotal,
                      }),
                    })
                      .then((r) => r.json())
                      .then((feeData) => {
                        if (feeData.error === "0" || feeData.error === 0) {
                          setProcessingFeeRow({
                            pct: feeData.txnCharge,
                            amount: parseFloat(feeData.transactionFee).toFixed(
                              2
                            ),
                          });
                          runningTotal = parseFloat(
                            parseFloat(
                              feeData.totalAmount || runningTotal
                            ).toFixed(2)
                          );
                        }
                        setTableType("paynow");
                        setPaynowRows({
                          desc: "",
                          qty: 1,
                          price: "",
                          total: runningTotal.toFixed(2),
                          currency: displayCurrency,
                        });
                      })
                      .catch(() => {
                        setTableType("paynow");
                        setPaynowRows({
                          desc: "",
                          qty: 1,
                          price: "",
                          total: runningTotal.toFixed(2),
                          currency: displayCurrency,
                        });
                      });
                  } else {
                    setTableType("paynow");
                    setPaynowRows({
                      desc: "",
                      qty: 1,
                      price: "",
                      total: runningTotal.toFixed(2),
                      currency: displayCurrency,
                    });
                  }
                }
              })
              .catch(() => {
                setTableType("paynow");
                setPaynowRows({
                  desc: "Order Payment",
                  qty: 1,
                  price: `$${fiatAmt.toFixed(2)}`,
                  total: fiatAmt.toFixed(2),
                  currency: "USD",
                });
              });
          } else {
            // No FX conversion needed - read the resolved currency amount directly
            let subtotal = 0;
            const prodRows = prods.map((p) => {
              const priceObj = p.prices?.[0];
              const ce =
                priceObj?.currencies?.find(
                  (c) => c.currency === displayCurrency
                ) ||
                priceObj?.currencies?.find((c) => c.default === true) ||
                priceObj?.currencies?.[0];
              const base = parseFloat(
                p.basePrice || p.price || p.amount || ce?.amount || 0
              );
              subtotal += base;
              return { name: p.name || "Product", base };
            });
            setOrderProductRows(prodRows);

            let handlingTotal = 0;
            if (hPct > 0) {
              handlingTotal = parseFloat(((subtotal * hPct) / 100).toFixed(2));
              setOrderHandlingFee({ pct: hPct, amount: handlingTotal });
            }

            let taxTotal = 0;
            if (tPct > 0) {
              taxTotal = parseFloat(((subtotal * tPct) / 100).toFixed(2));
              setOrderTaxFee({ pct: tPct, amount: taxTotal });
            }

            let runningTotal = parseFloat(
              (subtotal + handlingTotal + taxTotal).toFixed(2)
            );

            let discountAmt = 0;
            if (discountsList.length > 0) {
              let bestDiscount = null;
              let bestAmt = 0;
              for (const d of discountsList) {
                const minVal = parseFloat(d.minimumCartValue || 0);
                const pct = parseFloat(
                  d.discountPercentage || d.discountPercent || 0
                );
                if (runningTotal >= minVal && pct > 0) {
                  const amt = (runningTotal * pct) / 100;
                  if (!bestDiscount || amt > bestAmt) {
                    bestDiscount = d;
                    bestAmt = amt;
                  }
                }
              }
              if (bestDiscount) {
                discountAmt = parseFloat(bestAmt.toFixed(2));
                setOrderDiscount({
                  pct:
                    bestDiscount.discountPercentage ||
                    bestDiscount.discountPercent,
                  amount: discountAmt,
                  label: bestDiscount.profileName || "Discount",
                });
                runningTotal = parseFloat(
                  (runningTotal - discountAmt).toFixed(2)
                );
              }
            }

            const hasFee =
              profileData.isProcessingFeeApplied == 1 ||
              profileData.isProcessingFeeApplied === "1" ||
              profileData.isProcessingFeeApplied === true;
            if (hasFee && runningTotal > 0) {
              fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  merchantId: String(mId),
                  currencyId: "1",
                  amount: runningTotal,
                }),
              })
                .then((r) => r.json())
                .then((feeData) => {
                  if (feeData.error === "0" || feeData.error === 0) {
                    setProcessingFeeRow({
                      pct: feeData.txnCharge,
                      amount: parseFloat(feeData.transactionFee).toFixed(2),
                    });
                    runningTotal = parseFloat(
                      parseFloat(feeData.totalAmount || runningTotal).toFixed(2)
                    );
                  }
                  setTableType("paynow");
                  setPaynowRows({
                    desc: "",
                    qty: 1,
                    price: "",
                    total: runningTotal.toFixed(2),
                    currency: displayCurrency,
                  });
                })
                .catch(() => {
                  setTableType("paynow");
                  setPaynowRows({
                    desc: "",
                    qty: 1,
                    price: "",
                    total: runningTotal.toFixed(2),
                    currency: displayCurrency,
                  });
                });
            } else {
              setTableType("paynow");
              setPaynowRows({
                desc: "",
                qty: 1,
                price: "",
                total: runningTotal.toFixed(2),
                currency: displayCurrency,
              });
            }
          }

          const billing = profileData.billingProfiles?.[0];
          if (billing?.profileName) setMerchantName(billing.profileName);
        } else {
          setTableType("paynow");
          setPaynowRows({
            desc: "Order Payment",
            qty: 1,
            price: `$${fiatAmt.toFixed(2)}`,
            total: fiatAmt.toFixed(2),
            currency: "USD",
          });
        }

        if (crypto && fiatAmt > 0) {
          const baseCurrency = displayCurrency || "USD";
          fetch(
            `${WEBSERVICE}/merchant/getMarketPriceWithBaseCurrency?currency=${crypto}&baseCurrency=${baseCurrency}`
          )
            .then((r) => r.json())
            .then((priceRes) => {
              if (priceRes.error === "0" || priceRes.error === 0) {
                const cryptoPrice = parseFloat(priceRes.price);
                if (cryptoPrice > 0) {
                  // price = crypto per 1 fiat unit → multiply
                  const converted = (fiatAmt / cryptoPrice).toFixed(8);
                  const networkLabel = network
                    ? ` (${network === "ERC"
                      ? "ERC-20"
                      : network === "TRC"
                        ? "TRC-20"
                        : network
                    })`
                    : "";
                  setCryptoMarketPrice(cryptoPrice);
                  setCryptoEquivalent(`${converted} ${crypto}${networkLabel}`);
                  setPayAmount(`${converted} ${crypto}${networkLabel}`);
                  setPaynowRows((prev) => ({ ...prev, total: prev.total }));

                  const timerDecoded = {
                    currency_id: getCurrencyIdFromCode(crypto, network),
                    merchant_id: decoded?.merchant_id,
                    fiat_amount: fiatAmt,
                    is_processing_fee_applied:
                      decoded?.is_processing_fee_applied,
                  };
                  const currStrTimer = `${crypto}${networkLabel}`;
                  setIsPriceLoaded(true);
                  startTimerRef.current?.(timerDecoded, currStrTimer);
                  pollStatus();
                }
              }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
        } else {
          setLoading(false);
          pollStatus();
        }
      });
      return;
    }

    // ── Regular invoice flow with market price fetch ────────────────────────
    const assetCodeTimer = getAssetCode(decoded.currency_id, decoded);
    const currStrTimer =
      assetCodeTimer + (decoded.network ? ` ${decoded.network}` : "");

    fetch(`${WEBSERVICE}/merchant/getMarketPrice?counter=${assetCodeTimer}`)
      .then((r) => r.json())
      .then((priceRes) => {
        if (priceRes.error === "0" || priceRes.error === 0) {
          const cryptoPrice = parseFloat(priceRes.price);
          setCryptoMarketPrice(cryptoPrice);

          const baseAmt =
            parseFloat(decoded.fiat_amount || decoded.item_price || 0) /
            cryptoPrice;
          const qty = decoded.quantity || 1;
          let total = baseAmt * parseFloat(qty);

          setPayAmount(`${total.toFixed(6)} ${currStrTimer}`);

          if (cryptoPrice > 0 && decoded.fiat_amount) {
            const converted = (
              parseFloat(decoded.fiat_amount) / cryptoPrice
            ).toFixed(8);
            const networkLabel = decoded.network
              ? ` (${decoded.network === "ERC"
                ? "ERC-20"
                : decoded.network === "TRC"
                  ? "TRC-20"
                  : decoded.network
              })`
              : "";
            setCryptoEquivalent(
              `${converted} ${decoded.crypto || assetCodeTimer}${networkLabel}`
            );
          }

          const isPFee =
            decoded.is_processing_fee_applied == 1 ||
            decoded.is_processing_fee_applied == "1";

          const finalizeTimer = () => {
            console.log("Starting timer with decoded:", decoded);
            setIsPriceLoaded(true);
            localStorage.setItem("bill_bitcoins_invoice_timer_start_at", "900");
            setTimerSecs(900);
            // FIX: call via ref
            startTimerRef.current?.(decoded, currStrTimer);
            pollStatus();
            setLoading(false);
          };

          if (isPFee) {
            fetch(`${WEBSERVICE}/payment/calculateTransactionFee`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                merchantId: String(decoded.merchant_id),
                currencyId: String(decoded.currency_id),
                amount: parseFloat(total.toFixed(6)),
              }),
            })
              .then((r) => r.json())
              .then((feeData) => {
                if (feeData.error === "0") {
                  const grandTotal = parseFloat(feeData.totalAmount).toFixed(6);
                  setPayAmount(`${grandTotal} ${currStrTimer}`);
                  setProcessingFeeRow({
                    pct: feeData.txnCharge,
                    amount: parseFloat(feeData.transactionFee).toFixed(6),
                  });
                  setPaynowRows((prev) => ({ ...prev, total: grandTotal }));
                }
              })
              .finally(() => {
                finalizeTimer();
              });
          } else {
            finalizeTimer();
          }
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });

    const safetyTimer = setTimeout(() => setLoading(false), 8000);
    return () => clearTimeout(safetyTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timer display ──────────────────────────────────────────────────────────
  const mins = Math.max(0, Math.floor(timerSecs / 60));
  const secs = Math.max(0, timerSecs % 60);
  const timerDisplay = `${mins}:${String(secs).padStart(2, "0")}`;

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div
        style={{
          ...S.page,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={S.spinnerLg} />
          <p
            style={{
              marginTop: 12,
              fontSize: 14,
              color: "#6b7280",
              fontFamily: "'Roboto',sans-serif",
            }}
          >
            Please wait…
          </p>
        </div>
      </div>
    );
  }

  // ── Status card helper ─────────────────────────────────────────────────────
  const statusCards = {
    success: {
      bg: "#d1fae5",
      border: "#10b981",
      title: "Payment Received",
      body: `Payment processed. You paid ${amountPaid} ${currencyDisplay}.`,
    },
    success_over: {
      bg: "#d1fae5",
      border: "#10b981",
      title: "Payment Received",
      body: `Payment processed. You paid ${amountPaid} ${currencyDisplay}.`,
    },
    success_under: {
      bg: "#d1fae5",
      border: "#10b981",
      title: "Payment Received (Underpaid)",
      body: `Payment processed. You paid ${amountPaid} ${currencyDisplay}.`,
    },
    overpaid: {
      bg: "#d1fae5",
      border: "#10b981",
      title: "Payment Received",
      body: `Payment processed. You paid ${amountPaid} ${currencyDisplay}.`,
    },
    /*  overpaid: {
       bg: "#f3f4f6",
       border: "#6b7280",
       title: "Over Payment",
       body: `You paid ${amountPaid} ${currencyDisplay} instead of ${amountDue} ${currencyDisplay}.`,
     }, */
    underpaid: {
      bg: "#fef3c7",
      border: "#f59e0b",
      title: "Under Payment",
      body: `You paid ${amountPaid} ${currencyDisplay} instead of ${amountDue} ${currencyDisplay}.`,
    },
    failed: {
      bg: "#fee2e2",
      border: "#ef4444",
      title: "Payment Failed",
      body: "You have not paid any coin. If you made a payment please wait for confirmation in your email.",
    },
    expired: {
      bg: "#fee2e2",
      border: "#ef4444",
      title: "Payment Expired",
      body: "The invoice has expired. If you made a payment please wait for email confirmation.",
    },
    processing: {
      bg: "#eff6ff",
      border: "#284ccf",
      title: "Payment Processing",
      body: `${partPaid || "Payment is being processed. Please wait."}`,
    },
  };

  // ── Network label helper ───────────────────────────────────────────────────
  const networkLabel = urlNetwork
    ? ` (${urlNetwork === "ERC"
      ? "ERC-20"
      : urlNetwork === "TRC"
        ? "TRC-20"
        : urlNetwork
    })`
    : "";

  return (
    <div style={S.page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700&display=swap');
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        * { box-sizing:border-box; font-family:'Roboto',sans-serif!important; }
        body { margin:0; }
        @keyframes spin    { to { transform:rotate(360deg); } }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes scaleIn { from{transform:scaleX(0)} to{transform:scaleX(1)} }
        .pm-card,.pm-card * { color-scheme:light!important; }
        .pm-table { background:#fff!important; }
        .pm-table thead th { background:#f1f3f9!important; color:#374151!important; -webkit-text-fill-color:#374151!important; }
        .pm-table tbody tr { background:#fff!important; }
        .pm-table tbody tr td { background:#fff!important; color:#374151!important; -webkit-text-fill-color:#374151!important; border-color:#e2e6ef!important; }
        .pm-table tfoot tr th { background:#284ccf!important; color:#fff!important; -webkit-text-fill-color:#fff!important; }
        .pm-addr-link { color:#111827!important; -webkit-text-fill-color:#111827!important; text-decoration:none; font-family:'SFMono-Regular',Consolas,monospace!important; font-size:13px; font-weight:600; word-break:break-all; flex:1; cursor:pointer; }
        .pm-wallet-link { color:#284ccf!important; -webkit-text-fill-color:#284ccf!important; font-weight:600; text-decoration:none; cursor:pointer; }
        .pm-wallet-link:hover { text-decoration:underline; }
        .copy-btn:hover { background:rgba(40,76,207,0.10)!important; }
        .pm-timer-section, .pm-timer-section * {
    color: #111827 !important;
    -webkit-text-fill-color: #111827 !important;
}
.pm-timer-section strong {
    color: #284ccf !important;
    -webkit-text-fill-color: #284ccf !important;
}
        @media(max-width:768px){
          .pm-main-section { flex-direction:column-reverse!important; align-items:center!important; margin-bottom: 3px; }
          .pm-merchant-section { flex-direction:column!important; }
        }
        .iv-modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.45); z-index:1000; display:flex; align-items:center; justify-content:center; animation:fadeUp .2s ease; }
        .iv-modal-box { background:#fff; border-radius:16px; padding:40px 36px; max-width:420px; width:90%; text-align:center; box-shadow:0 24px 64px rgba(0,0,0,0.18); }
      `}</style>

      {/* ── Invoice Not Found Modal ── */}
      {showErrorModal && (
        <div
          className="iv-modal-overlay"
          onClick={() => setShowErrorModal(false)}
        >
          <div className="iv-modal-box" onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3
              style={{
                margin: "0 0 10px",
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
                fontFamily: "'Roboto',sans-serif",
              }}
            >
              Invoice Not Found
            </h3>
            <p
              style={{
                margin: "0 0 24px",
                fontSize: 14,
                color: "#6b7280",
                lineHeight: 1.6,
                fontFamily: "'Roboto',sans-serif",
              }}
            >
              The invoice details could not be found or the payment link is
              invalid or corrupted. Please check the link and try again.
            </p>
            <button
              onClick={() => setShowErrorModal(false)}
              style={{
                padding: "12px 32px",
                background: "linear-gradient(135deg,#284ccf,#102ab3)",
                color: "#fff",
                WebkitTextFillColor: "#fff",
                border: "none",
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Roboto',sans-serif",
                width: "100%",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {parseError && <div style={S.errorBanner}>⚠ {parseError}</div>}

      {/* ── Header ── */}
      <div style={S.header}>
        <div style={S.headerIcon}>
          {coinImage ? (
            <img
              src={coinImage}
              alt={coinName}
              style={{
                width: 34,
                height: 34,
                filter: "brightness(0) invert(1)",
              }}
            />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
        </div>
        <h2 style={S.headerTitle}>
          Pay with <span style={S.coinName}>{coinName}</span>
        </h2>
        <p style={S.headerSub}>Complete your payment securely</p>
      </div>

      {/* ── Card 1 — Payment ── */}
      <div className="pm-card" style={S.card}>
        <div style={S.cardAccent} />
        <div style={S.cardBody}>
          {(!paymentStatus || paymentStatus === "underpaid" || paymentStatus === "success_under") && (
            <div className="pm-main-section" style={S.mainSection}>
              {/* Left — Info */}
              <div style={S.payInfo}>
                <p style={S.sendLabel}>Please send exactly</p>
                {remainingAmount && (paymentStatus === "underpaid" || paymentStatus === "success_under") ? (
                  <>
                    <p style={S.sendAmount}>
                      {remainingAmount} {urlCrypto}{networkLabel}
                    </p>
                    <div style={{
                      marginBottom: 16,
                      padding: "8px 14px",
                      borderRadius: 8,
                      background: "rgba(245,158,11,0.08)",
                      border: "1px solid rgba(245,158,11,0.25)",
                      fontSize: 12,
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                      Remaining balance after partial payment of {amountPaid} {urlCrypto}
                    </div>
                  </>
                ) : (
                  <p style={S.sendAmount}>{payAmount}</p>
                )}

                <p style={S.addrLabel}>
                  To this address
                </p>
                {/* Address section */}
                <div style={S.addrContainer}>
                  <span className="pm-addr-link" onClick={copyAddress}>
                    {txAddress && txAddress !== "" ? txAddress : "Loading..."}
                  </span>
                  <button
                    className="copy-btn"
                    onClick={copyAddress}
                    style={S.copyBtn}
                    title="Copy address"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  </button>
                  {copied && <span style={S.copiedMsg}>Copied!</span>}
                </div>

                {/* Memo/Tag section for HCX and XRP */}
                {(obj?.crypto === 'HCX' || obj?.crypto === 'XRP') && (
                  <div>
                    <p style={S.memoLabel}>
                      {obj?.crypto === 'HCX' ? 'Memo' : 'Tag'}:
                    </p>
                    <div style={S.memoContainer}>
                      <div style={S.memoValueContainer}>
                        <span style={S.memoValue}>
                          {obj?.chargeId || obj?.charge_id || 'N/A'}
                        </span>
                        <button
                          className="copy-btn"
                          onClick={() => {
                            const memoValue = obj?.chargeId || obj?.charge_id || '';
                            if (memoValue) {
                              navigator.clipboard.writeText(memoValue);
                              setMemoCopied(true);
                              setTimeout(() => setMemoCopied(false), 1500);
                            }
                          }}
                          style={S.copyBtnSmall}
                          title={`Copy ${obj?.crypto === 'HCX' ? 'Memo' : 'Tag'}`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </button>
                        {memoCopied && <span style={S.memoCopiedMsg}>Copied!</span>}
                      </div>
                    </div>
                  </div>
                )}


                <p style={S.walletLinkSection}>
                  Open in{" "}
                  <span className="pm-wallet-link" onClick={redirectToExchange}>
                    {walletLoading ? "Loading…" : `${exchangeName} Wallet`}
                  </span>{" "}
                  or pay through external wallet
                </p>

                {/* FIX: show timer when isPriceLoaded OR when address is available */}
                {(isPriceLoaded || txAddress) && (
                  <div className="pm-timer-section" style={S.timerSection}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 8,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#284ccf"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{ flexShrink: 0, marginTop: 1 }}
                      >
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>
                        The price is locked for{" "}
                        <strong style={{ color: "#284ccf", fontSize: "16px" }}>
                          {timerDisplay}
                        </strong>{" "}
                        minutes. After it expires, the crypto amount will be
                        recalculated using the latest market price. This invoice
                        expires in 24 hours, and payment must be completed
                        within that time.
                      </span>
                    </div>
                    <ul
                      style={{
                        paddingLeft: 14,
                        marginTop: 10,
                        marginBottom: 0,
                      }}
                    >
                      <li>
                        You may make multiple payments to complete the full
                        invoice amount.
                      </li>
                      <li>
                        Once the total amount paid is equal to or greater than
                        the amount shown, the invoice will be marked as paid.
                      </li>
                      <li>
                        Any overpayment may not be refunded by the merchant.
                      </li>
                    </ul>
                    {partPaid && (
                      <div
                        style={{
                          marginTop: 10,
                          padding: "10px 14px",
                          background: "rgba(16,185,129,0.10)",
                          border: "1px solid rgba(16,185,129,0.2)",
                          borderRadius: 8,
                          color: "#065f46",
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {partPaid}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right — QR: FIX: pure React img tag, no DOM manipulation */}
              <div style={S.qrSection}>
                {txAddress && txAddress !== "Loading..." && txAddress !== "" ? (
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
                      txAddress
                    )}`}
                    alt="QR Code"
                    style={{
                      width: 180,
                      height: 180,
                      borderRadius: 16,
                      border: "2px solid rgba(40,76,207,.15)",
                      padding: 10,
                      background: "#fff",
                      boxShadow: "0 4px 16px rgba(40,76,207,.10)",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      ...S.qrImage,
                      background: "#f1f3f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    Loading QR...
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status card */}
          {paymentStatus &&
            statusCards[paymentStatus] &&
            (() => {
              const sc = statusCards[paymentStatus];
              return (
                <div
                  style={{
                    background: sc.bg,
                    border: `1px solid ${sc.border}`,
                    borderRadius: 12,
                    padding: 24,
                    animation: "fadeUp .4s ease",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 8px",
                      fontWeight: 700,
                      color: "#111827",
                      fontSize: 16,
                    }}
                  >
                    {sc.title}
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: 14,
                      color: "#374151",
                      lineHeight: 1.6,
                    }}
                  >
                    {sc.body}
                  </p>
                  {returnUrl && (
                    <a
                      href={returnUrl}
                      style={{
                        display: "inline-block",
                        marginTop: 16,
                        padding: "10px 24px",
                        background: "#284ccf",
                        color: "#fff",
                        borderRadius: 10,
                        textDecoration: "none",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      Return to Merchant Website
                    </a>
                  )}
                </div>
              );
            })()}
        </div>
      </div>

      {/* ── Card 2 — Merchant + Order Details ── */}
      <div className="pm-card" style={S.card}>
        <div style={S.cardAccent} />
        <div style={S.cardBody}>
          <div className="pm-merchant-section" style={S.merchantSection}>
            {/* Merchant Info */}
            <div style={S.merchantDetails}>
              <h4 style={S.merchantName}>{companyName}</h4>
              {merchantAddr && <p style={S.merchantText}>{merchantAddr}</p>}
              {supportEmail && (
                <p style={S.merchantText}>
                  <b style={{ color: "#374151" }}>Email: </b>
                  {supportEmail}
                </p>
              )}
              {supportPhone && (
                <p style={S.merchantText}>
                  <b style={{ color: "#374151" }}>Phone: </b>
                  {supportPhone}
                </p>
              )}
            </div>

            {/* Order Details */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {tableType === "paynow" && (
                <div
                  style={{
                    overflowX: "auto",
                    WebkitOverflowScrolling: "touch",
                    marginBottom: 12,
                  }}
                >
                  <table className="pm-table" style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Item</th>
                        <th style={{ ...S.th, textAlign: "right" }}>
                          {orderCurrency || paynowRows.currency || "USD"}
                        </th>
                        {cryptoEquivalent && (
                          <th style={{ ...S.th, textAlign: "right" }}>
                            {urlCrypto}
                            {networkLabel}
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {orderProductRows.length > 0 ? (
                        orderProductRows.map((p, i) => (
                          <tr key={i}>
                            <td style={S.td}>
                              <span
                                style={{ fontWeight: 600, color: "#111827" }}
                              >
                                {p.name}
                              </span>
                            </td>
                            <td style={{ ...S.td, textAlign: "right" }}>
                              {p.base.toFixed(2)}
                            </td>
                            {cryptoEquivalent && (
                              <td
                                style={{
                                  ...S.td,
                                  textAlign: "right",
                                  color: "#f59e0b",
                                  fontWeight: 600,
                                }}
                              >
                                {cryptoMarketPrice > 0
                                  ? `${(p.base / cryptoMarketPrice).toFixed(
                                    8
                                  )} ${urlCrypto}`
                                  : "—"}
                              </td>
                            )}
                          </tr>
                        ))
                      ) : paynowRows.desc ? (
                        <tr>
                          <td style={S.td}>{paynowRows.desc}</td>
                          <td style={{ ...S.td, textAlign: "right" }}>
                            {paynowRows.price}
                          </td>
                          {cryptoEquivalent && (
                            <td style={{ ...S.td, textAlign: "right" }}>—</td>
                          )}
                        </tr>
                      ) : null}

                      {orderHandlingFee && (
                        <tr>
                          <td
                            style={{ ...S.td, color: "#6b7280", fontSize: 12 }}
                          >
                            Handling Fee ({orderHandlingFee.pct}%)
                          </td>
                          <td
                            style={{
                              ...S.td,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {orderHandlingFee.amount.toFixed(2)}
                          </td>
                          {cryptoEquivalent && (
                            <td
                              style={{
                                ...S.td,
                                textAlign: "right",
                                color: "#f59e0b",
                                fontWeight: 600,
                              }}
                            >
                              {cryptoMarketPrice > 0
                                ? `${(
                                  orderHandlingFee.amount / cryptoMarketPrice
                                ).toFixed(8)} ${urlCrypto}`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      )}

                      {orderTaxFee && (
                        <tr>
                          <td
                            style={{ ...S.td, color: "#6b7280", fontSize: 12 }}
                          >
                            Tax ({orderTaxFee.pct}%)
                          </td>
                          <td
                            style={{
                              ...S.td,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {orderTaxFee.amount.toFixed(2)}
                          </td>
                          {cryptoEquivalent && (
                            <td
                              style={{
                                ...S.td,
                                textAlign: "right",
                                color: "#f59e0b",
                                fontWeight: 600,
                              }}
                            >
                              {cryptoMarketPrice > 0
                                ? `${(
                                  orderTaxFee.amount / cryptoMarketPrice
                                ).toFixed(8)} ${urlCrypto}`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      )}

                      {orderDiscount && (
                        <tr>
                          <td
                            style={{
                              ...S.td,
                              color: "#10b981",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                          >
                            Discount ({orderDiscount.pct}%)
                            <span
                              style={{
                                fontSize: 10,
                                marginLeft: 6,
                                opacity: 0.8,
                              }}
                            >
                              {orderDiscount.label}
                            </span>
                          </td>
                          <td
                            style={{
                              ...S.td,
                              textAlign: "right",
                              fontWeight: 700,
                              color: "#10b981",
                            }}
                          >
                            -{orderDiscount.amount.toFixed(2)}
                          </td>
                          {cryptoEquivalent && (
                            <td
                              style={{
                                ...S.td,
                                textAlign: "right",
                                fontWeight: 700,
                                color: "#10b981",
                              }}
                            >
                              {cryptoMarketPrice > 0
                                ? `-${(
                                  orderDiscount.amount / cryptoMarketPrice
                                ).toFixed(8)} ${urlCrypto}`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      )}

                      {processingFeeRow && (
                        <tr>
                          <td
                            style={{ ...S.td, color: "#6b7280", fontSize: 12 }}
                          >
                            Processing Fee ({processingFeeRow.pct}%)
                          </td>
                          <td
                            style={{
                              ...S.td,
                              textAlign: "right",
                              fontWeight: 600,
                            }}
                          >
                            {processingFeeRow.amount}
                          </td>
                          {cryptoEquivalent && (
                            <td
                              style={{
                                ...S.td,
                                textAlign: "right",
                                color: "#f59e0b",
                                fontWeight: 600,
                              }}
                            >
                              {cryptoMarketPrice > 0
                                ? `${(
                                  parseFloat(processingFeeRow.amount) /
                                  cryptoMarketPrice
                                ).toFixed(8)} ${urlCrypto}`
                                : "—"}
                            </td>
                          )}
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th style={S.tfootCell}>TOTAL</th>
                        <th style={{ ...S.tfootCell, textAlign: "right" }}>
                          {paynowRows.total}{" "}
                          {orderCurrency || paynowRows.currency}
                        </th>
                        {cryptoEquivalent && (
                          <th style={{ ...S.tfootCell, textAlign: "right" }}>
                            ≈ {cryptoEquivalent}
                          </th>
                        )}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <p
        style={{
          textAlign: "center",
          fontSize: 12,
          color: "#9ca3af",
          marginTop: 24,
          fontFamily: "'Roboto',sans-serif",
        }}
      >
        🔒 Payments secured by PayBito
      </p>
    </div>
  );
}

// ─── Asset helpers ─────────────────────────────────────────────────────────────
function getAssetCode(cid, decoded) {
  if (typeof window.getAssetDetails === "function") {
    return (
      window.getAssetDetails(cid)?.assetCode || decoded?.currency || "CRYPTO"
    );
  }
  const map = {
    2: "BTC",
    3: "BCH",
    4: "LTC",
    5: "ETH",
    6: "ZEC",
    7: "DOGE",
    8: "HCX",
    9: "XRP",
    10: "USDC",
    11: "USDT",
    16: "USDT",
  };
  return map[cid] || decoded?.currency || "CRYPTO";
}

function getAssetName(cid, decoded) {
  if (typeof window.getAssetDetails === "function") {
    return (
      window.getAssetDetails(cid)?.assetName || decoded?.currency || "Crypto"
    );
  }
  const map = {
    2: "Bitcoin",
    3: "Bitcoin Cash",
    4: "Litecoin",
    5: "Ethereum",
    6: "Zcash",
    7: "Dogecoin",
    8: "HCX",
    9: "Ripple",
    10: "USD Coin",
    11: "Tether",
    16: "Tether",
  };
  return map[cid] || decoded?.currency || "Crypto";
}

function getAssetImage(cid) {
  if (typeof window.getAssetDetails === "function")
    return window.getAssetDetails(cid)?.assetImage || "";
  return "";
}

function getCurrencyIdFromCode(code, network = null) {
  const map = {
    BTC: 2,
    BCH: 3,
    LTC: 4,
    ETH: 5,
    ZEC: 6,
    DOGE: 7,
    HCX: 8,
    XRP: 9,
    USDC: 10,
    USDT: 11,
    USDT_ERC: 16,
    USDT_TRC: 16,
  };

  if (code === "USDT") {
    if (network === "ERC" || network === "ERC20") return 16;
    if (network === "TRC" || network === "TRC20") return 16;
    return 11;
  }

  return map[code] || 2;
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page: {
    background: "linear-gradient(135deg,#e8ecf8 0%,#f0f2fa 50%,#eef0f7 100%)",
    minHeight: "100vh",
    fontFamily: "'Roboto',sans-serif",
    padding: "40px 20px 60px",
  },
  errorBanner: {
    maxWidth: 920,
    margin: "0 auto 16px",
    background: "#fef3c7",
    border: "1px solid #f59e0b",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 13,
    color: "#92400e",
    fontWeight: 500,
  },
  header: {
    textAlign: "center",
    marginBottom: 32,
    animation: "fadeUp .5s ease",
  },
  headerIcon: {
    width: 64,
    height: 64,
    borderRadius: "50%",
    background: "linear-gradient(135deg,#284ccf,#102ab3)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    boxShadow: "0 8px 24px rgba(40,76,207,.30)",
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 4px",
  },
  coinName: { background: "linear-gradient(135deg,#284ccf,#102ab3)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
  headerSub: { fontSize: 14, color: "#6b7280", margin: 0 },
  card: {
    maxWidth: 920,
    margin: "0 auto 24px",
    background: "#fff",
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 30px rgba(0,0,0,.08)",
    border: "1px solid rgba(40,76,207,.08)",
    overflow: "hidden",
    animation: "fadeUp .5s ease .1s both",
  },
  cardAccent: {
    height: 4,
    background: "linear-gradient(90deg,#284ccf,#102ab3,#10b981)",
    transformOrigin: "left",
    animation: "scaleIn .8s ease .3s both",
  },
  cardBody: { padding: 32, background: "#fff", backgroundColor: "#fff" },
  mainSection: { display: "flex", gap: 32, alignItems: "flex-start", marginBottom: 13 },
  payInfo: { flex: 1 },
  sendLabel: {
    fontSize: 14,
    color: "#6b7280",
    margin: "0 0 4px",
    fontFamily: "'Roboto',sans-serif",
  },
  sendAmount: {
    fontSize: 24,
    fontWeight: 700,
    margin: "0 0 20px",
    background: "linear-gradient(135deg,#284ccf,#102ab3)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontFamily: "'Roboto',sans-serif",
  },
  addrLabel: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#6b7280",
    margin: "0 0 8px",
  },
  addrContainer: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#f1f3f9",
    border: "1px solid #e2e6ef",
    borderRadius: 10,
    padding: "12px 16px",
    marginBottom: 12,
  },
  copyBtn: {
    background: "#284ccf",
    border: "none",
    padding: 8,
    borderRadius: 8,
    cursor: "pointer",
    display: "flex",
    flexShrink: 0,
    transition: "background .2s",
  },
  copiedMsg: {
    color: "#10b981",
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  memoSection: {
    fontSize: 13,
    color: "#284ccf",
    fontWeight: 600,
    marginBottom: 12,
  },
  walletLinkSection: {
    fontSize: 13,
    color: "#374151",
    marginBottom: 20,
    lineHeight: 1.6,
  },
  timerSection: {
    padding: "12px 16px",
    fontSize: 13,
    color: "#111827 !important",
    lineHeight: 1.6,
    background:
      "linear-gradient(135deg,rgba(40,76,207,.06),rgba(40,76,207,.02))",
    border: "1px solid rgba(40,76,207,.10)",
    borderRadius: 10,
    WebkitTextFillColor: "#111827",
  },
  qrSection: { flexShrink: 0, textAlign: "center" },
  qrImage: {
    width: 180,
    height: 180,
    borderRadius: 16,
    border: "2px solid rgba(40,76,207,.15)",
    padding: 10,
    background: "#fff",
    boxShadow: "0 4px 16px rgba(40,76,207,.10)",
  },
  merchantSection: { display: "flex", gap: 32, alignItems: "flex-start" },
  merchantDetails: { flex: "0 0 280px" },
  merchantName: {
    fontSize: 16,
    fontWeight: 700,
    color: "#111827",
    margin: "0 0 8px",
  },
  merchantText: {
    fontSize: 13,
    color: "#6b7280",
    margin: "0 0 6px",
    lineHeight: 1.5,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    border: "1px solid #e2e6ef",
    borderRadius: 12,
    fontSize: 13,
    overflow: "hidden",
  },
  th: {
    background: "#f1f3f9",
    color: "#374151",
    WebkitTextFillColor: "#374151",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    padding: "12px 16px",
    border: "none",
    textAlign: "left",
  },
  td: {
    padding: "12px 16px",
    color: "#374151",
    background: "#fff",
    borderTop: "1px solid #e2e6ef",
  },
  tfootCell: {
    padding: "14px 16px",
    textAlign: "left",
    fontSize: 14,
    fontWeight: 700,
    background: "#284ccf",
    color: "#fff",
    WebkitTextFillColor: "#fff",
  },
  spinnerLg: {
    display: "inline-block",
    width: 36,
    height: 36,
    border: "3px solid rgba(40,76,207,.2)",
    borderTop: "3px solid #284ccf",
    borderRadius: "50%",
    animation: "spin .8s linear infinite",
    margin: "0 auto",
  },
  memoContainer: {
    marginTop: 8,
    marginBottom: 12,
    padding: "10px 16px",
    background: "#f1f3f9",
    border: "1px solid #e2e6ef",
    borderRadius: 10,
  },
  memoLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: "#6b7280",
    margin: "0 0 6px 0",
  },
  memoValueContainer: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  memoValue: {
    flex: 1,
    fontSize: 13,
    fontWeight: 600,
    color: "#284ccf",
    fontFamily: "'SFMono-Regular', Consolas, monospace",
    wordBreak: "break-all",
  },
  copyBtnSmall: {
    background: "#284ccf",
    border: "none",
    padding: 6,
    borderRadius: 6,
    cursor: "pointer",
    display: "flex",
    flexShrink: 0,
    transition: "background .2s",
  },
  memoCopiedMsg: {
    color: "#10b981",
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: "nowrap",
    marginLeft: 8,
  },
};
